import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { QuestionMarkCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const FAQPage = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const faqData = [
        {
            category: "General",
            questions: [
                {
                    question: "What is AircoreDynamics?",
                    answer: "AircoreDynamics is an interactive educational platform focused on aviation, aerospace, and aerodynamics. We provide comprehensive courses, research articles, and learning materials to help students and professionals understand the principles of flight, aircraft design, and aerospace technologies."
                },
                {
                    question: "Who can use this platform?",
                    answer: "Our platform is designed for everyone interested in aviation and aerospace - from beginners with no prior knowledge to advanced learners and professionals. Whether you're a student, aviation enthusiast, engineer, or researcher, you'll find valuable content suited to your level."
                },
                {
                    question: "Is the platform free?",
                    answer: "We offer both free and premium content. Basic access to articles, some courses, and research materials is free. Premium courses with certificates and advanced content may require a subscription or one-time payment."
                }
            ]
        },
        {
            category: "Courses",
            questions: [
                {
                    question: "How do I enroll in a course?",
                    answer: "To enroll in a course, simply browse our course catalog, select the course you're interested in, and click the 'Enroll' button. You'll need to create a free account if you haven't already. Once enrolled, you'll have immediate access to all course materials."
                },
                {
                    question: "Can I learn at my own pace?",
                    answer: "Yes! All our courses are self-paced. You can start, pause, and resume your learning whenever it's convenient for you. There are no strict deadlines, allowing you to balance your education with your other commitments."
                },
                {
                    question: "What happens after I complete a course?",
                    answer: "Upon successfully completing a course and passing any required quizzes or assessments, you'll receive a digital certificate of completion. This certificate includes a unique verification ID and can be shared on your resume, LinkedIn, or other professional platforms."
                },
                {
                    question: "Are the courses accredited?",
                    answer: "Our courses provide valuable knowledge and skills in aviation and aerospace. While our certificates demonstrate course completion and learning achievement, they are not equivalent to university degrees or professional licenses. However, they are excellent for professional development and demonstrating expertise to employers."
                }
            ]
        },
        {
            category: "Certificates",
            questions: [
                {
                    question: "How do I get a certificate?",
                    answer: "Certificates are automatically generated when you successfully complete all course modules and pass the required assessments. You can download your certificate from your profile page or the course completion page."
                },
                {
                    question: "Can I verify a certificate?",
                    answer: "Yes! Every certificate comes with a unique certificate ID. Anyone can verify the authenticity of a certificate by entering this ID on our certificate verification page. This ensures the credibility of your achievements."
                },
                {
                    question: "Are certificates digital or physical?",
                    answer: "Our certificates are digital, which makes them easy to share online and reduces environmental impact. You can download them as PDF files, share them on social media, or include them in your digital portfolio."
                }
            ]
        },
        {
            category: "Technical",
            questions: [
                {
                    question: "What browsers are supported?",
                    answer: "Our platform works best on modern browsers including Google Chrome, Mozilla Firefox, Safari, and Microsoft Edge. We recommend keeping your browser updated to the latest version for the best experience."
                },
                {
                    question: "Can I access the platform on mobile devices?",
                    answer: "Yes! Our platform is fully responsive and works on smartphones and tablets. You can learn on the go, whether you're using iOS, Android, or any other mobile operating system."
                },
                {
                    question: "I'm having technical issues. What should I do?",
                    answer: "If you encounter any technical problems, please try clearing your browser cache and cookies first. If the issue persists, contact our support team at support@aircoredynamics.com with a description of the problem and any error messages you're seeing."
                }
            ]
        },
        {
            category: "Account & Privacy",
            questions: [
                {
                    question: "How do I create an account?",
                    answer: "Click the 'Register' button in the top navigation menu. Fill in your name, email address, and create a secure password. You'll receive a confirmation email to verify your account. Once verified, you can start learning immediately!"
                },
                {
                    question: "Is my personal information safe?",
                    answer: "Absolutely. We take data security very seriously. All passwords are encrypted, and we use industry-standard SSL/TLS encryption for data transmission. We never share your personal information with third parties without your consent. Read our Privacy Policy for more details."
                },
                {
                    question: "Can I delete my account?",
                    answer: "Yes, you have the right to delete your account at any time. Go to your profile settings and select 'Delete Account.' Please note that this action is permanent and will remove all your progress, certificates, and personal data."
                },
                {
                    question: "I forgot my password. How can I reset it?",
                    answer: "Click on the 'Forgot Password?' link on the login page. Enter your registered email address, and we'll send you a password reset link. Follow the instructions in the email to create a new password."
                }
            ]
        },
        {
            category: "Content",
            questions: [
                {
                    question: "How often is new content added?",
                    answer: "We regularly update our platform with new courses, articles, and research materials. New content is typically added weekly, and we continuously update existing courses to reflect the latest developments in aviation and aerospace technology."
                },
                {
                    question: "Can I suggest topics for new courses?",
                    answer: "Yes! We love hearing from our community. If you have suggestions for new courses or topics you'd like to learn about, please contact us through our contact form or email us at info@aircoredynamics.com. Your feedback helps us create content that matters to you."
                },
                {
                    question: "Are there prerequisites for advanced courses?",
                    answer: "Some advanced courses may require foundational knowledge. Prerequisites are clearly listed on each course page. We recommend starting with beginner courses if you're new to aviation or aerospace to build a solid foundation."
                }
            ]
        }
    ];

    // Prepare FAQ schema data
    const faqSchemaData = faqData.flatMap(category => 
        category.questions.map(item => ({
            question: item.question,
            answer: item.answer
        }))
    );

    // Breadcrumb data
    const breadcrumbData = [
        { name: 'Home', url: '/' },
        { name: 'FAQ', url: '/faq' }
    ];

    return (
        <>
            <SEO 
                title="Frequently Asked Questions - AircoreDynamics"
                description="Find answers to common questions about AircoreDynamics courses, certificates, platform features, and more. Get help with technical issues and account management."
                keywords="FAQ, frequently asked questions, help, support, AircoreDynamics help, course questions, certificate verification, technical support"
                image="https://aircoredynamics.com/og-image.png"
                url="/faq"
                type="website"
                breadcrumb={breadcrumbData}
                faq={faqSchemaData}
            />
            <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="container mx-auto px-6 py-12 max-w-5xl"
        >
            {/* Header */}
            <div className="text-center mb-12">
                <QuestionMarkCircleIcon className="h-16 w-16 mx-auto mb-4 text-light-primary dark:text-dark-primary" />
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                    Frequently Asked <span className="gradient-text">Questions</span>
                </h1>
                <p className="text-light-text/70 dark:text-dark-text/70 max-w-2xl mx-auto">
                    Find answers to common questions about our platform, courses, and services.
                </p>
            </div>

            {/* FAQ Categories */}
            <div className="space-y-8">
                {faqData.map((category, categoryIndex) => (
                    <motion.div
                        key={categoryIndex}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: categoryIndex * 0.1 }}
                    >
                        <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                            <span className="w-2 h-8 bg-gradient-to-b from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-400 rounded-full"></span>
                            {category.category}
                        </h2>
                        
                        <div className="space-y-4">
                            {category.questions.map((item, questionIndex) => {
                                const globalIndex = `${categoryIndex}-${questionIndex}`;
                                const isOpen = openIndex === globalIndex;

                                return (
                                    <div
                                        key={questionIndex}
                                        className="card overflow-hidden"
                                    >
                                        <button
                                            onClick={() => toggleFAQ(globalIndex)}
                                            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                                        >
                                            <span className="font-semibold text-lg pr-4">
                                                {item.question}
                                            </span>
                                            <ChevronDownIcon
                                                className={`h-5 w-5 flex-shrink-0 text-light-primary dark:text-dark-primary transition-transform duration-300 ${
                                                    isOpen ? 'rotate-180' : ''
                                                }`}
                                            />
                                        </button>
                                        
                                        <AnimatePresence>
                                            {isOpen && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="px-6 pb-4 pt-2 text-light-text/80 dark:text-dark-text/80 leading-relaxed border-t border-light-border dark:border-dark-border">
                                                        {item.answer}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Contact CTA */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="mt-16 text-center card p-8 bg-gradient-to-br from-light-card to-light-bg dark:from-dark-card dark:to-dark-bg"
            >
                <h3 className="text-2xl font-bold mb-4">
                    Still have questions?
                </h3>
                <p className="text-light-text/70 dark:text-dark-text/70 mb-6">
                    Can't find what you're looking for? Our support team is here to help.
                </p>
                <Link
                    to="/contact"
                    className="btn-primary inline-block"
                >
                    Contact Support
                </Link>
            </motion.div>

            {/* Back Button */}
            <div className="text-center mt-8">
                <Link 
                    to="/" 
                    className="btn-secondary inline-block"
                >
                    Back to Home
                </Link>
            </div>
        </motion.div>
        </>
    );
};

export default FAQPage;
